/**
 * Helper methods for interacting with fully qualified MDX Member names, e.g. [Date].[year].[year]
 */
export class MdxMemberUtil {

    public static formatPartsForLabel(parts: string[]): string[] {

        if (parts[0] === "Date" && parts[1] === "day of week" && parts[2] === "day of week") {
            if (parts.length > 3) {
                switch (parts[3]) {
                    case "1":
                        parts[3] = "Mon";
                        break;
                    case "2":
                        parts[3] = "Tue";
                        break;
                    case "3":
                        parts[3] = "Wed";
                        break;
                    case "4":
                        parts[3] = "Thu";
                        break;
                    case "5":
                        parts[3] = "Fri";
                        break;
                    case "6":
                        parts[3] = "Sat";
                        break;
                    case "7":
                        parts[3] = "Sun";
                        break;
                }

            }
        }
        else if (parts[0] === "Date" && parts[1] === "month of year" && parts[2] === "month of year") {
            if (parts.length > 3) {
                switch (parts[3]) {
                    case "1":
                        parts[3] = "Jan";
                        break;
                    case "2":
                        parts[3] = "Feb";
                        break;
                    case "3":
                        parts[3] = "Mar";
                        break;
                    case "4":
                        parts[3] = "Apr";
                        break;
                    case "5":
                        parts[3] = "May";
                        break;
                    case "6":
                        parts[3] = "Jun";
                        break;
                    case "7":
                        parts[3] = "Jul";
                        break;
                    case "8":
                        parts[3] = "Aug";
                        break;
                    case "9":
                        parts[3] = "Sep";
                        break;
                    case "10":
                        parts[3] = "Oct";
                        break;
                    case "11":
                        parts[3] = "Nov";
                        break;
                    case "12":
                        parts[3] = "Dec";
                        break;
                }

            }

        }
        else if (parts[0] === "Time" && parts[1] === "hour of day" && parts[2] === "hour of day") {
            if (parts.length > 3) {
                if (parts[3].length == 1) {
                    parts[3] = "hr 0" + parts[3];
                }
                else {
                    parts[3] = "hr " + parts[3];
                }
            }

            if (parts.length > 4) {
                parts[4] = parts[4].replace("_", ":");
            }
        }
        else if (parts[0] === "Date" && parts[1] === "week of year" && parts[2] === "week of year") {
            if (parts.length > 3) {
                parts[3] = "wk " + parts[3];
            }
        }

        return parts;
    }

    /**
     * Formats a fully qualified member name to be displayed as a label
     * @param {string} name
     * @returns {string} Formatted string
     */
    public static formatForLabel(name: string): string {
        if (name === "last12Months") {
            return "Last 12 Months";
        }

        if (name.startsWith("[Date].[day of week].[day of week]")) {
            switch (this.formatForLabelDepth(name, 1)) {
                case "1":
                    return "Mon";
                case "2":
                    return "Tue";
                case "3":
                    return "Wed";
                case "4":
                    return "Thu";
                case "5":
                    return "Fri";
                case "6":
                    return "Sat";
                case "7":
                    return "Sun";
            }
        }

        return this.formatForLabelDepth(name, 1);
    }


    public static getDimension(member: string): string {
        const members: string[] = member.split(".");
        return members.slice(0, members.length - 1).join(".");
    }

    public static getDimensionValue(member: string): string {
        const parts: string [] = MdxMemberUtil.getParts(member);
        return parts[parts.length - 1];
    }

    /**
     * Splits a name into it's parts
     * @param {string} name
     * @returns {string[]}
     */
    public static getParts(name: string): string[] {
        const results: string[] = [];
        const split: string[] = name.split("].[");

        split.forEach((s: string) => {
            results.push(this.trimSquareBrackets(s));
        });

        return results;
    }

    /**
     * Reduces the passed in name to the specified depth
     * @param {string} name
     * @param depth Depth starting from the first member of the hierarchy
     * @returns {string}
     */
    public static getMemberAtDepth(name: string, depth: number): string {
        const parts: string[] = MdxMemberUtil.getParts(name);
        let t: string = "[" + parts[0] + "].[" + parts[1] + "].[" + parts[2] + "]";
        for (let i = 0; i < depth; i++) {
            t = t + ".[" + parts[3 + i] + "]";
        }

        return t;
    }

    /**
     * Indicates if the name represents a name
     * @param {string} name
     */
    public static isLevel(name: string): boolean {
        return MdxMemberUtil.getParts(name).length === 3;
    }

    /**
     * Indicates if the name represents a member
     */
    public static isMember(name: string): boolean {
        return MdxMemberUtil.getParts(name).length > 3;
    }

    /**
     * Retrieves the dimension/hierarchy parts of the name
     * @param {string} name
     * @returns {string}
     */
    public static getHierarchy(name: string): string {
        const parts: string[] = MdxMemberUtil.getParts(name);
        return "[" + parts[0] + "].[" + parts[1] + "]";
    }

    /**
     * Maps a member from a hierarchy to a logical child hierarchy. Hierarchies are from the same dimension
     * This allows more fine grained controls to be used when drilling to lower levels.
     * E.g. [Outlet].[state].[state].[QLD].[Milton] maps to [Outlet].[location].[location]
     * @param {string} member Member to map
     * @param childHierarchies
     * @returns {string} Mapped member
     */
    public static getHierarchyForMember(member: string, childHierarchies: { [key: string]: string; }): string {
        if (!childHierarchies) {
            return member;
        }

        const parts = MdxMemberUtil.getParts(member);
        const depth = parts.length - 3;
        if (depth === 2) {
            let v = childHierarchies["[" + parts[0] + "].[" + parts[1] + "]"];
            if (v) {
                v = v + ".[" + parts[parts.length - 1] + "]";
                return v;
            }

        }

        return member;
    }

    private static formatForLabelDepth(name: string, depth: number): string {

        let result: string = "";

        const split: string[] = name.split("].[");

        for (let i = 0; i < depth; i++) {

            if (i != 0) {
                result = result + "/";
            }
            result = result + this.trimSquareBrackets(split[split.length - (depth - i)]);
        }

        return result;
    }

    private static trimSquareBrackets(s: string): string {
        if (!s) {
            return "";
        }

        if (s.startsWith("[")) {
            s = s.substr(1);
        }

        if (s.endsWith("]")) {
            s = s.substr(0, s.length - 1);
        }

        return s;
    }

    /**
     * Derives a key for dimension and hierarchy parts of a name
     * @param {string} name
     * @returns {string}
     */
    public static getDimensionKey(name: string): string {
        if (name.startsWith("[Measures]")) {
            return "Measures";
        }
        const split: string[] = name.split("].[");
        return this.trimSquareBrackets(split[0]) + ";" + this.trimSquareBrackets(split[1]);
    }
}
